(() => {
  if (window.__SSS_QV_STANDALONE__) return;
  window.__SSS_QV_STANDALONE__ = true;

  const ROOT_ID = "sss-quick-view-standalone";
  const currency = () => window.Shopify?.currency?.active || "USD";
  const money = (value) => {
    const amount = Number(value || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: currency() }).format(amount);
    } catch (_) {
      return "$" + amount.toFixed(2);
    }
  };

  const esc = (value) => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

  const optionNames = (product) =>
    (product.options || []).map((option) =>
      typeof option === "string" ? option : option?.name || ""
    );

  const normalizeMedia = (product) => {
    if (Array.isArray(product.media) && product.media.length) {
      return product.media
        .filter((item) => !item.media_type || item.media_type === "image")
        .map((item) => ({
          id: item.id,
          src: item.preview_image?.src || item.src || item.url || "",
          alt: item.alt || item.preview_image?.alt || ""
        }))
        .filter((item) => item.src);
    }
    return (product.images || [])
      .map((image, index) => ({
        id: "image-" + index,
        src: typeof image === "string" ? image : image?.src || image?.url || "",
        alt: typeof image === "string" ? "" : image?.alt || ""
      }))
      .filter((item) => item.src);
  };

  const variantImageSrc = (variant) =>
    variant?.featured_media?.preview_image?.src ||
    variant?.featured_media?.src ||
    variant?.featured_image?.src ||
    (typeof variant?.featured_image === "string" ? variant.featured_image : "") ||
    "";

  const fileCore = (src) => {
    if (!src) return "";
    let file = String(src).split("?")[0].split("/").pop() || "";
    try { file = decodeURIComponent(file); } catch (_) {}
    return file
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/_[0-9]+x[0-9]*$/i, "");
  };

  const familyKey = (src) =>
    fileCore(src)
      .replace(/[-_](?:0?1|0?2|0?3|0?5|0?6|0?7)$/i, "")
      .replace(/[-_](?:1|2|3|5|6|7)$/i, "");

  const viewNumber = (src) => {
    const match = fileCore(src).match(/[-_](0?[123567])$/i);
    return match ? Number(match[1]) : 999;
  };

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const mediaForVariant = (product, variant) => {
    const media = normalizeMedia(product);
    if (!media.length) return [];

    const featured = variantImageSrc(variant);
    const featuredCore = fileCore(featured);
    const family = familyKey(featured);
    let matches = family ? media.filter((item) => familyKey(item.src) === family) : [];

    const names = optionNames(product).map((name) => name.toLowerCase());
    const colorIndex = names.findIndex((name) => name.includes("color"));
    const color = colorIndex >= 0 ? normalizeText(variant?.options?.[colorIndex]) : "";

    if (matches.length < 2 && color) {
      const colorNeedle = " " + color + " ";
      const altMatches = media.filter((item) => {
        const alt = normalizeText(item.alt);
        return alt && (" " + alt + " ").includes(colorNeedle);
      });
      if (altMatches.length > matches.length) matches = altMatches;
    }

    if (!matches.length && featured) {
      const exact = media.find((item) => fileCore(item.src) === featuredCore);
      if (exact) matches = [exact];
    }

    if (!matches.length && featured) {
      matches = [{
        id: variant?.featured_media?.id || variant?.featured_image?.id || "variant-featured",
        src: featured,
        alt: variant?.featured_media?.alt || variant?.featured_image?.alt || ""
      }];
    }

    if (!matches.length) return [];

    const seen = new Set();
    const unique = matches.filter((item) => {
      if (!item.src || seen.has(item.src)) return false;
      seen.add(item.src);
      return true;
    });

    const hasView01 = unique.some((item) => viewNumber(item.src) === 1);
    const hasOtherMultiView = unique.some((item) => [2, 3, 5, 6, 7].includes(viewNumber(item.src)));
    const priority = hasView01 && hasOtherMultiView
      ? [1, 5, 2, 3, 6, 7]
      : [5, 2, 3, 6, 7, 1];

    unique.sort((a, b) => {
      const av = viewNumber(a.src);
      const bv = viewNumber(b.src);
      const ar = priority.includes(av) ? priority.indexOf(av) : 100 + av;
      const br = priority.includes(bv) ? priority.indexOf(bv) : 100 + bv;
      return ar - br;
    });

    const hasRecognizedViews = unique.some((item) => viewNumber(item.src) !== 999);
    if (!hasRecognizedViews && featured) {
      const index = unique.findIndex((item) => fileCore(item.src) === featuredCore);
      if (index > 0) unique.unshift(unique.splice(index, 1)[0]);
    }

    return unique;
  };

  const parseRating = (html) => {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const rating = doc.querySelector(".pdp-rating-summary");
    const label = rating?.getAttribute("aria-label") || "";
    const match = label.match(/([\d.]+)\s+out of\s+[\d.]+\s+stars?\s+from\s+([\d,]+)\s+reviews?/i);
    if (!match) return null;
    return { value: match[1], count: match[2] };
  };

  const createRoot = () => {
    let root = document.getElementById(ROOT_ID);
    if (root) return root;

    root = document.createElement("div");
    root.id = ROOT_ID;
    root.className = "sss-qv2";
    root.setAttribute("aria-hidden", "true");
    root.innerHTML =
      '<div class="sss-qv2__backdrop" data-qv-close></div>' +
      '<section class="sss-qv2__dialog" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button class="sss-qv2__close" type="button" aria-label="Close quick view" data-qv-close>×</button>' +
        '<div class="sss-qv2__body" data-qv-body></div>' +
      '</section>';

    document.body.appendChild(root);

    root.addEventListener("click", (event) => {
      if (event.target.closest("[data-qv-close]")) close();
    });

    return root;
  };

  let active = null;
  let lastFocus = null;
  const quickViewProductCache = new Map();

  const getQuickViewProduct = (jsonUrl) => {
    if (quickViewProductCache.has(jsonUrl)) {
      return quickViewProductCache.get(jsonUrl);
    }

    const request = fetch(jsonUrl, {
      credentials: "same-origin",
      cache: "force-cache",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Product request failed");
        return response.json();
      })
      .catch((error) => {
        quickViewProductCache.delete(jsonUrl);
        throw error;
      });

    quickViewProductCache.set(jsonUrl, request);
    return request;
  };

  const injectQuickViewRating = (root, rating) => {
    if (!root || !rating || root.querySelector(".sss-qv2__rating")) return;
    const title = root.querySelector(".sss-qv2__title");
    if (!title) return;

    const ratingEl = document.createElement("div");
    ratingEl.className = "sss-qv2__rating";
    ratingEl.setAttribute(
      "aria-label",
      rating.value + " out of 5 from " + rating.count + " reviews"
    );
    ratingEl.innerHTML =
      '<span class="sss-qv2__rating-star">★</span>' +
      "<span>" + esc(rating.value) + "</span>" +
      '<span class="sss-qv2__rating-dot">·</span>' +
      "<span>" + esc(rating.count) + " reviews</span>";

    title.insertAdjacentElement("afterend", ratingEl);
  };

  const close = () => {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;
    root.classList.remove("is-open");
    root.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("sss-qv2-open");
    active = null;
    window.setTimeout(() => lastFocus?.focus?.(), 30);
  };

  const setMainImage = (root, src, alt, clicked) => {
    const main = root.querySelector("[data-qv-main-image]");
    if (!main || !src) return;
    main.src = src;
    main.alt = alt || "";
    main.removeAttribute("srcset");
    root.querySelectorAll("[data-qv-thumb]").forEach((button) =>
      button.classList.toggle("is-active", button === clicked)
    );
  };

  const availableVariantForOptions = (product, selected) =>
    product.variants.find((variant) =>
      variant.available &&
      variant.options.every((value, index) =>
        selected[index] == null || selected[index] === "" || String(value) === String(selected[index])
      )
    ) || null;

  const exactVariantForOptions = (product, selected) =>
    product.variants.find((variant) =>
      variant.options.every((value, index) => String(value) === String(selected[index] ?? ""))
    ) || null;

  const buildColorValues = (product, colorIndex) => {
    const values = [];
    product.variants.forEach((variant) => {
      const value = variant.options?.[colorIndex];
      if (value && !values.includes(value)) values.push(value);
    });
    return values;
  };

  const buildOptionValues = (product, index) => {
    const values = [];
    product.variants.forEach((variant) => {
      const value = variant.options?.[index];
      if (value && !values.includes(value)) values.push(value);
    });
    return values;
  };

  const renderGallery = () => {
    if (!active) return;
    const { root, product, selected, colorIndex } = active;
    const colorVariant =
      availableVariantForOptions(product, selected.map((value, index) =>
        index === colorIndex ? value : null
      )) ||
      availableVariantForOptions(product, selected) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];

    const media = mediaForVariant(product, colorVariant);
    const stage = root.querySelector("[data-qv-gallery]");
    if (!stage || !media.length) return;

    stage.innerHTML =
      '<div class="sss-qv2__image-stage">' +
        '<img class="sss-qv2__main-image" data-qv-main-image src="' + esc(media[0].src) + '" alt="' + esc(media[0].alt || product.title) + '" decoding="async">' +
      '</div>' +
      (media.length > 1 ? '<div class="sss-qv2__thumbs" aria-label="Product images">' +
        media.map((item, index) =>
          '<button type="button" class="sss-qv2__thumb' + (index === 0 ? " is-active" : "") + '" data-qv-thumb data-src="' + esc(item.src) + '" data-alt="' + esc(item.alt || product.title) + '" aria-label="View image ' + (index + 1) + '">' +
            '<img src="' + esc(item.src) + '" alt="" loading="lazy" decoding="async">' +
          '</button>'
        ).join("") +
      '</div>' : "");
  };

  const updateVariantState = () => {
    if (!active) return;
    const { root, product, selected, optionNames: names, colorIndex, sizeIndex } = active;

    root.querySelectorAll("[data-qv-option]").forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.optionValue;
      const test = selected.slice();
      test[index] = value;
      const available = !!availableVariantForOptions(product, test);
      button.disabled = !available;
      button.classList.toggle("is-active", String(selected[index] ?? "") === String(value));
    });

    if (sizeIndex >= 0) {
      root.querySelectorAll('[data-qv-option][data-option-index="' + sizeIndex + '"]').forEach((button) => {
        const test = selected.slice();
        test[sizeIndex] = button.dataset.optionValue;
        const available = !!availableVariantForOptions(product, test);
        button.disabled = !available;
      });
    }

    const exact = exactVariantForOptions(product, selected);
    const displayVariant = exact || availableVariantForOptions(product, selected) ||
      availableVariantForOptions(product, selected.map((value, index) => index === colorIndex ? value : null)) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];

    const price = root.querySelector("[data-qv-price]");
    if (price && displayVariant) {
      const compare = Number(displayVariant.compare_at_price || 0);
      const current = Number(displayVariant.price || 0);
      price.innerHTML =
        '<span class="sss-qv2__price-current">' + money(current) + '</span>' +
        (compare > current ? '<s class="sss-qv2__price-compare">' + money(compare) + '</s>' : "");
    }

    const add = root.querySelector("[data-qv-add]");
    const status = root.querySelector("[data-qv-status]");
    const requiresSize = sizeIndex >= 0 && buildOptionValues(product, sizeIndex).length > 1;
    const sizeChosen = sizeIndex < 0 || !!selected[sizeIndex];
    const ready = !!exact?.available && (!requiresSize || sizeChosen);

    if (add) {
      add.disabled = !ready;
      add.dataset.variantId = ready ? String(exact.id) : "";
      add.textContent = ready ? "Add to Bag" : (requiresSize && !sizeChosen ? "Select a Size" : "Unavailable");
    }
    if (status) status.textContent = "";

    const details = root.querySelector("[data-qv-details]");
    if (details && displayVariant) {
      const base = product.url || active.productPath;
      details.href = base + (String(base).includes("?") ? "&" : "?") + "variant=" + displayVariant.id;
    }
  };

  const renderOptions = () => {
    if (!active) return;
    const { root, product, selected, optionNames: names, colorIndex, sizeIndex } = active;
    const host = root.querySelector("[data-qv-options]");
    if (!host) return;

    const groups = names.map((name, index) => {
      const values = index === colorIndex ? buildColorValues(product, index) : buildOptionValues(product, index);
      if (!values.length) return "";

      if (index === colorIndex) {
        return (
          '<div class="sss-qv2__option-group">' +
            '<div class="sss-qv2__option-heading"><span>' + esc(name) + '</span><span class="sss-qv2__selected-value" data-qv-selected-color>' + esc(selected[index] || "") + '</span></div>' +
            '<div class="sss-qv2__color-rail" data-qv-color-rail>' +
              '<button type="button" class="sss-qv2__color-nav sss-qv2__color-nav--prev" data-qv-color-nav="-1" aria-label="Show previous colors">' +
                '<svg viewBox="0 0 12 20" aria-hidden="true"><path d="M10 2 2 10l8 8"/></svg>' +
              '</button>' +
              '<div class="sss-qv2__colors" data-qv-color-track>' +
                values.map((value) => {
                  const variant =
                    product.variants.find((v) => v.available && String(v.options?.[index]) === String(value)) ||
                    product.variants.find((v) => String(v.options?.[index]) === String(value));
                  const image = variantImageSrc(variant);
                  return (
                    '<button type="button" class="sss-qv2__color' + (String(selected[index]) === String(value) ? " is-active" : "") + '" data-qv-option data-option-index="' + index + '" data-option-value="' + esc(value) + '" aria-label="Select ' + esc(value) + '">' +
                      '<span class="sss-qv2__color-image">' + (image ? '<img src="' + esc(image) + '" alt="" loading="lazy" decoding="async">' : "") + '</span>' +
                      '<span class="sss-qv2__color-name">' + esc(value) + '</span>' +
                    '</button>'
                  );
                }).join("") +
              '</div>' +
              '<button type="button" class="sss-qv2__color-nav sss-qv2__color-nav--next" data-qv-color-nav="1" aria-label="Show more colors">' +
                '<svg viewBox="0 0 12 20" aria-hidden="true"><path d="m2 2 8 8-8 8"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>'
        );
      }

      const label = index === sizeIndex ? "Select a Size" : name;
      return (
        '<div class="sss-qv2__option-group">' +
          '<div class="sss-qv2__option-heading"><span>' + esc(label) + '</span></div>' +
          '<div class="sss-qv2__pills">' +
            values.map((value) =>
              '<button type="button" class="sss-qv2__pill' + (String(selected[index] || "") === String(value) ? " is-active" : "") + '" data-qv-option data-option-index="' + index + '" data-option-value="' + esc(value) + '">' +
                esc(value) +
              '</button>'
            ).join("") +
          '</div>' +
        '</div>'
      );
    }).join("");

    host.innerHTML = groups;
    window.requestAnimationFrame(() => updateColorRailControls(root));
  };

  const updateColorRailControls = (root = active?.root) => {
    if (!root) return;

    root.querySelectorAll("[data-qv-color-rail]").forEach((rail) => {
      const track = rail.querySelector("[data-qv-color-track]");
      const prev = rail.querySelector('[data-qv-color-nav="-1"]');
      const next = rail.querySelector('[data-qv-color-nav="1"]');
      if (!track || !prev || !next) return;

      const refresh = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const overflows = maxScroll > 3;
        rail.classList.toggle("is-overflowing", overflows);
        prev.disabled = !overflows || track.scrollLeft <= 3;
        next.disabled = !overflows || track.scrollLeft >= maxScroll - 3;
      };

      if (track.dataset.qvRailBound !== "true") {
        track.dataset.qvRailBound = "true";
        let frame = 0;
        track.addEventListener(
          "scroll",
          () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(refresh);
          },
          { passive: true }
        );
      }

      refresh();
    });
  };

  const render = () => {
    if (!active) return;
    const { root, product, selected, rating } = active;
    const vendor = product.vendor || "";
    const title = product.title || "";
    const initialVariant =
      product.variants.find((v) => String(v.id) === String(active.initialVariantId)) ||
      product.variants.find((v) => v.available) ||
      product.variants[0];

    root.querySelector("[data-qv-body]").innerHTML =
      '<div class="sss-qv2__layout">' +
        '<div class="sss-qv2__media" data-qv-gallery></div>' +
        '<div class="sss-qv2__info">' +
          '<div class="sss-qv2__eyebrow">' + esc(vendor) + '</div>' +
          '<h2 class="sss-qv2__title">' + esc(title) + '</h2>' +
          (rating ? '<div class="sss-qv2__rating" aria-label="' + esc(rating.value + ' out of 5 from ' + rating.count + ' reviews') + '"><span class="sss-qv2__rating-star">★</span><span>' + esc(rating.value) + '</span><span class="sss-qv2__rating-dot">·</span><span>' + esc(rating.count) + ' reviews</span></div>' : "") +
          '<div class="sss-qv2__price" data-qv-price>' +
            '<span class="sss-qv2__price-current">' + money(initialVariant?.price || product.price) + '</span>' +
          '</div>' +
          '<div class="sss-qv2__rule"></div>' +
          '<div data-qv-options></div>' +
          '<div class="sss-qv2__actions">' +
            '<button type="button" class="sss-qv2__add" data-qv-add disabled>Select a Size</button>' +
            '<p class="sss-qv2__status" data-qv-status role="status" aria-live="polite"></p>' +
            '<a class="sss-qv2__details" data-qv-details href="' + esc(product.url || active.productPath) + '">View Full Details <span aria-hidden="true">→</span></a>' +
          '</div>' +
        '</div>' +
      '</div>';

    renderOptions();
    renderGallery();
    updateVariantState();
  };

  const open = async (button) => {
    lastFocus = button;
    const root = createRoot();
    const body = root.querySelector("[data-qv-body]");
    body.innerHTML = '<div class="sss-qv2__loading">Loading…</div>';
    root.classList.add("is-open");
    root.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("sss-qv2-open");

    const rawUrl = button.getAttribute("data-product-url") || button.closest("a")?.href || "";
    if (!rawUrl) {
      body.innerHTML = '<div class="sss-qv2__error">Unable to load this product.</div>';
      return;
    }

    const url = new URL(rawUrl, location.origin);
    const jsonUrl = url.pathname.replace(/\/$/, "") + ".js";
    const initialVariantId = url.searchParams.get("variant");

    let product;
    try {
      product = await getQuickViewProduct(jsonUrl);
    } catch (error) {
      body.innerHTML = '<div class="sss-qv2__error">Unable to load this product.</div>';
      return;
    }
    const names = optionNames(product);
    const lowerNames = names.map((name) => name.toLowerCase());
    const colorIndex = lowerNames.findIndex((name) => name.includes("color"));
    const sizeIndex = lowerNames.findIndex((name) => name.includes("size"));
    const initialVariant =
      product.variants.find((variant) => String(variant.id) === String(initialVariantId)) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];

    const selected = Array(names.length).fill(null);
    initialVariant?.options?.forEach((value, index) => {
      selected[index] = index === sizeIndex ? null : value;
    });

    if (colorIndex >= 0 && !selected[colorIndex]) {
      selected[colorIndex] = initialVariant?.options?.[colorIndex] || buildColorValues(product, colorIndex)[0] || null;
    }

    active = {
      root,
      product,
      productPath: url.pathname,
      initialVariantId,
      selected,
      optionNames: names,
      colorIndex,
      sizeIndex,
      rating: null
    };

    render();
    root.querySelector(".sss-qv2__close")?.focus();

    fetch(url.pathname + url.search, {
      credentials: "same-origin",
      cache: "force-cache",
    })
      .then((response) => (response.ok ? response.text() : ""))
      .then((html) => {
        if (!html || !active || active.root !== root || active.product !== product) return;
        const rating = parseRating(html);
        if (rating) {
          active.rating = rating;
          injectQuickViewRating(root, rating);
        }
      })
      .catch(() => {});
  };

  const prefetchQuickViewProduct = (button) => {
    const rawUrl = button?.getAttribute("data-product-url");
    if (!rawUrl) return;
    const url = new URL(rawUrl, location.origin);
    const jsonUrl = url.pathname.replace(/\/$/, "") + ".js";
    getQuickViewProduct(jsonUrl).catch(() => {});
  };

  document.addEventListener(
    "pointerover",
    (event) => {
      const button = event.target.closest(".quick-add-view[data-product-url]");
      if (button) prefetchQuickViewProduct(button);
    },
    { passive: true }
  );

  document.addEventListener(
    "focusin",
    (event) => {
      const button = event.target.closest(".quick-add-view[data-product-url]");
      if (button) prefetchQuickViewProduct(button);
    },
    { passive: true }
  );

  document.addEventListener(
    "touchstart",
    (event) => {
      const button = event.target.closest(".quick-add-view[data-product-url]");
      if (button) prefetchQuickViewProduct(button);
    },
    { passive: true }
  );

  document.addEventListener("click", (event) => {
    const button = event.target.closest(".quick-add-view[data-product-url]");
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    open(button);
  }, true);

  document.addEventListener("click", (event) => {
    if (!active) return;

    const colorNav = event.target.closest("[data-qv-color-nav]");
    if (colorNav && active.root.contains(colorNav) && !colorNav.disabled) {
      event.preventDefault();
      const rail = colorNav.closest("[data-qv-color-rail]");
      const track = rail?.querySelector("[data-qv-color-track]");
      if (track) {
        const direction = Number(colorNav.dataset.qvColorNav || 1);
        const distance = Math.max(150, Math.round(track.clientWidth * 0.72));
        track.scrollBy({ left: direction * distance, behavior: "smooth" });
      }
      return;
    }

    const thumb = event.target.closest("[data-qv-thumb]");
    if (thumb && active.root.contains(thumb)) {
      event.preventDefault();
      setMainImage(active.root, thumb.dataset.src, thumb.dataset.alt, thumb);
      return;
    }

    const option = event.target.closest("[data-qv-option]");
    if (option && active.root.contains(option) && !option.disabled) {
      event.preventDefault();
      const index = Number(option.dataset.optionIndex);
      const value = option.dataset.optionValue;
      active.selected[index] = value;

      if (index === active.colorIndex && active.sizeIndex >= 0) {
        const sizeValue = active.selected[active.sizeIndex];
        if (sizeValue) {
          const test = active.selected.slice();
          const exists = !!availableVariantForOptions(active.product, test);
          if (!exists) active.selected[active.sizeIndex] = null;
        }
      }

      renderOptions();
      renderGallery();
      updateVariantState();

      window.requestAnimationFrame(() => {
        const selectedColor = active.root.querySelector(
          ".sss-qv2__colors .sss-qv2__color.is-active"
        );
        const track = selectedColor?.closest("[data-qv-color-track]");
        if (selectedColor && track) {
          const left = selectedColor.offsetLeft;
          const right = left + selectedColor.offsetWidth;
          const visibleLeft = track.scrollLeft;
          const visibleRight = visibleLeft + track.clientWidth;

          if (left < visibleLeft || right > visibleRight) {
            track.scrollTo({
              left: Math.max(
                0,
                left - (track.clientWidth - selectedColor.offsetWidth) / 2
              ),
              behavior: "smooth",
            });
          }
        }
        updateColorRailControls(active.root);
      });
    }
  });

  document.addEventListener("click", async (event) => {
    const add = event.target.closest("[data-qv-add]");
    if (!add || !active?.root.contains(add) || add.disabled) return;

    event.preventDefault();
    const variantId = Number(add.dataset.variantId);
    if (!variantId) return;

    const status = active.root.querySelector("[data-qv-status]");
    const original = add.textContent;
    add.disabled = true;
    add.textContent = "Adding…";

    try {
      const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || "Unable to add this item.");
      add.textContent = "Added to Bag";
      if (status) status.textContent = "Added to your bag.";
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      window.setTimeout(() => {
        if (add.isConnected) {
          add.disabled = false;
          add.textContent = original;
        }
      }, 1600);
    } catch (error) {
      add.disabled = false;
      add.textContent = original;
      if (status) status.textContent = error.message || "Unable to add this item.";
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && active) close();
  });
})();